import React, { useState, useEffect } from 'react'
import {
  Row,
  Col,
  Card,
  Statistic,
  Typography,
  Button,
  Table,
  Tag,
  Space,
  Spin
} from 'antd'
import {
  BarcodeOutlined,
  InboxOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  SyncOutlined,
  UserOutlined,
  RightOutlined,
  ReloadOutlined
} from '@ant-design/icons'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { RootState } from '../store'

const { Text, Title } = Typography

// Dashboard统计数据类型
interface DashboardStats {
  skuStats: {
    total: number
    todayNew: number
  }
  packingListStats: {
    total: number
    unpacked: number
    packing: number
    completed: number
  }
  todayProgress: {
    startedLists: number
    completedLists: number
    completedBoxes: number
  }
  recentOperations: Array<{
    id: string
    type: string
    operator: string
    packingNumber: string
    time: string
  }>
}

const Dashboard: React.FC = () => {
  const { user } = useSelector((state: RootState) => state.auth)
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [stats, setStats] = useState<DashboardStats>({
    skuStats: { total: 0, todayNew: 0 },
    packingListStats: { total: 0, unpacked: 0, packing: 0, completed: 0 },
    todayProgress: { startedLists: 0, completedLists: 0, completedBoxes: 0 },
    recentOperations: []
  })

  const getRoleWelcomeMessage = (role: string) => {
    const messages = {
      ADMIN: '管理员，您可以管理系统的所有功能',
      BUSINESS: '业务员，您可以管理SKU和装箱清单',
      PACKER: '装箱员，您可以进行装箱操作',
    }
    return messages[role as keyof typeof messages] || '欢迎使用扫码装箱系统'
  }

  // 加载仪表盘数据
  const loadDashboardData = async () => {
    try {
      setLoading(true)

      // 调用真实API
      const { getDashboardStatistics } = await import('../services/dashboard')
      const data = await getDashboardStatistics()
      setStats(data)

    } catch (error) {
      console.error('加载仪表盘数据失败:', error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadDashboardData()
  }, [])

  // 获取操作类型显示文本
  const getOperationTypeText = (type: string) => {
    const typeMap: Record<string, string> = {
      START_LIST: '开始清单',
      COMPLETE_LIST: '完成清单',
      START_BOX: '开始装箱',
      SEAL_BOX: '封箱',
      FORCE_SEAL: '强制封箱',
      SKIP_BOX: '跳过本箱',
      UNPACK_BOX: '拆箱',
      SCAN_ITEM: '扫码录入',
      SCAN_SUCCESS: '扫码成功',
      SCAN_ERROR: '扫码错误',
      BOX_COMPLETE: '完成装箱',
      LIST_START: '开始清单',
      LIST_COMPLETE: '完成清单'
    }
    return typeMap[type] || type
  }

  // 最近操作记录表格列
  const operationColumns = [
    {
      title: '操作类型',
      dataIndex: 'type',
      key: 'type',
      width: 100,
      render: (type: string) => (
        <Tag color="blue">{getOperationTypeText(type)}</Tag>
      )
    },
    {
      title: '装箱号',
      dataIndex: 'packingNumber',
      key: 'packingNumber',
      width: 120
    },
    {
      title: '操作员',
      dataIndex: 'operator',
      key: 'operator',
      width: 100,
      render: (text: string) => (
        <Space>
          <UserOutlined />
          {text}
        </Space>
      )
    },
    {
      title: '操作时间',
      dataIndex: 'time',
      key: 'time',
      width: 120
    }
  ]

  return (
    <div style={{
      padding: '24px 32px',
      background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
      minHeight: '100vh'
    }}>

      {/* 欢迎区域 */}
      <div style={{
        background: 'white',
        borderRadius: '12px',
        padding: '32px',
        marginBottom: '24px',
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
        textAlign: 'center'
      }}>
        <Title level={2} style={{ margin: 0, color: '#1890ff', marginBottom: 16 }}>
          欢迎回来，{user?.username}
        </Title>
        <Text type="secondary" style={{ fontSize: '16px' }}>
          {getRoleWelcomeMessage(user?.role || '')}
        </Text>
        <div style={{ marginTop: 16 }}>
          <Text type="secondary" style={{ fontSize: '14px' }}>
            {new Date().toLocaleDateString('zh-CN', {
              year: 'numeric',
              month: 'long',
              day: 'numeric',
              weekday: 'long'
            })}
          </Text>
        </div>
      </div>

      {/* 核心统计卡片 */}
      <Row gutter={[24, 24]} style={{ marginBottom: 24 }}>
        {/* SKU统计 */}
        <Col xs={24} sm={12} lg={6}>
          <Card hoverable>
            <Statistic
              title="SKU总数"
              value={stats.skuStats.total}
              prefix={<BarcodeOutlined style={{ fontSize: '28px', color: '#1890ff' }} />}
              valueStyle={{ color: '#1890ff', fontSize: '32px', fontWeight: 'bold' }}
              loading={loading}
            />
            <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid #f0f0f0' }}>
              <Text type="secondary" style={{ fontSize: '13px' }}>
                今日新增: <Text strong style={{ color: '#52c41a' }}>{stats.skuStats.todayNew}</Text>
              </Text>
            </div>
          </Card>
        </Col>

        {/* 装箱清单总数 */}
        <Col xs={24} sm={12} lg={6}>
          <Card hoverable>
            <Statistic
              title="装箱清单总数"
              value={stats.packingListStats.total}
              prefix={<InboxOutlined style={{ fontSize: '28px', color: '#722ed1' }} />}
              valueStyle={{ color: '#722ed1', fontSize: '32px', fontWeight: 'bold' }}
              loading={loading}
            />
            <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid #f0f0f0' }}>
              <Space size="small">
                <Tag color="default">未开始 {stats.packingListStats.unpacked}</Tag>
                <Tag color="processing">进行中 {stats.packingListStats.packing}</Tag>
              </Space>
            </div>
          </Card>
        </Col>

        {/* 已完成清单 */}
        <Col xs={24} sm={12} lg={6}>
          <Card hoverable>
            <Statistic
              title="已完成清单"
              value={stats.packingListStats.completed}
              prefix={<CheckCircleOutlined style={{ fontSize: '28px', color: '#52c41a' }} />}
              valueStyle={{ color: '#52c41a', fontSize: '32px', fontWeight: 'bold' }}
              loading={loading}
            />
            <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid #f0f0f0' }}>
              <Text type="secondary" style={{ fontSize: '13px' }}>
                完成率: <Text strong>{stats.packingListStats.total > 0 ? Math.round((stats.packingListStats.completed / stats.packingListStats.total) * 100) : 0}%</Text>
              </Text>
            </div>
          </Card>
        </Col>

        {/* 待处理清单 */}
        <Col xs={24} sm={12} lg={6}>
          <Card hoverable>
            <Statistic
              title="待处理清单"
              value={stats.packingListStats.unpacked + stats.packingListStats.packing}
              prefix={<ClockCircleOutlined style={{ fontSize: '28px', color: '#faad14' }} />}
              valueStyle={{ color: '#faad14', fontSize: '32px', fontWeight: 'bold' }}
              loading={loading}
            />
            <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid #f0f0f0' }}>
              <Text type="secondary" style={{ fontSize: '13px' }}>
                <SyncOutlined spin style={{ marginRight: 4 }} />
                需要关注
              </Text>
            </div>
          </Card>
        </Col>
      </Row>

      {/* 今日装箱进度 */}
      <Row gutter={[24, 24]} style={{ marginBottom: 24 }}>
        <Col xs={24}>
          <Card 
            title="今日装箱进度"
            extra={
              <Button 
                type="text" 
                icon={<ReloadOutlined />} 
                onClick={loadDashboardData}
                loading={loading}
              >
                刷新
              </Button>
            }
          >
            <Row gutter={[24, 16]}>
              <Col xs={24} sm={8}>
                <div style={{
                  background: 'linear-gradient(135deg, #5b7cdb 0%, #6366f1 100%)',
                  padding: '24px',
                  borderRadius: '12px',
                  color: 'white',
                  textAlign: 'center',
                  boxShadow: '0 4px 12px rgba(91, 124, 219, 0.25)'
                }}>
                  <div style={{ fontSize: '36px', fontWeight: 'bold', marginBottom: '8px' }}>
                    {stats.todayProgress.startedLists}
                  </div>
                  <div style={{ fontSize: '14px', opacity: 0.9 }}>今日开始清单</div>
                </div>
              </Col>
              <Col xs={24} sm={8}>
                <div style={{
                  background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                  padding: '24px',
                  borderRadius: '12px',
                  color: 'white',
                  textAlign: 'center',
                  boxShadow: '0 4px 12px rgba(16, 185, 129, 0.25)'
                }}>
                  <div style={{ fontSize: '36px', fontWeight: 'bold', marginBottom: '8px' }}>
                    {stats.todayProgress.completedLists}
                  </div>
                  <div style={{ fontSize: '14px', opacity: 0.9 }}>今日完成清单</div>
                </div>
              </Col>
              <Col xs={24} sm={8}>
                <div style={{
                  background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                  padding: '24px',
                  borderRadius: '12px',
                  color: 'white',
                  textAlign: 'center',
                  boxShadow: '0 4px 12px rgba(245, 158, 11, 0.25)'
                }}>
                  <div style={{ fontSize: '36px', fontWeight: 'bold', marginBottom: '8px' }}>
                    {stats.todayProgress.completedBoxes}
                  </div>
                  <div style={{ fontSize: '14px', opacity: 0.9 }}>今日完成箱数</div>
                </div>
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>

      {/* 最近操作记录 */}
      <Row gutter={[24, 24]}>
        <Col xs={24}>
          <Card
            title="最近操作记录"
            extra={<Button type="link" onClick={() => navigate('/packing')}>查看装箱管理 <RightOutlined /></Button>}
          >
            <Spin spinning={loading}>
              <Table
                columns={operationColumns}
                dataSource={stats.recentOperations}
                rowKey="id"
                pagination={false}
                size="middle"
                locale={{ emptyText: '暂无操作记录' }}
              />
            </Spin>
          </Card>
        </Col>
      </Row>
    </div>
  )
}

export default Dashboard